import type { ActivityTrackingEvent } from '../types/dashboard';
import { logger } from './logger';

/**
 * Local storage keys for persistence
 */
export const STORAGE_KEYS = {
  GUIDANCE_STATE: 'autar_guidance_state',
  GUIDANCE_PREFERENCES: 'autar_guidance_preferences',
  GUIDANCE_INTERACTIONS: 'autar_guidance_interactions',
  ACTIVITY_METRICS: 'autar_activity_metrics',
  SESSION_DATA: 'autar_session_data',
} as const;

/**
 * Session management
 */
export class SessionManager {
  private sessionId: string;
  private sessionStartTime: number;
  private activityEvents: ActivityTrackingEvent[] = [];

  constructor() {
    this.sessionId = this.generateSessionId();
    this.sessionStartTime = Date.now();
    this.trackEvent('session_start');
  }

  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  getSessionId(): string {
    return this.sessionId;
  }

  getSessionDuration(): number {
    return Date.now() - this.sessionStartTime;
  }

  trackEvent(type: ActivityTrackingEvent['type'], data?: Record<string, unknown>): void {
    const event: ActivityTrackingEvent = {
      type,
      timestamp: new Date(),
      data,
      sessionId: this.sessionId,
    };

    this.activityEvents.push(event);

    // Persist to localStorage for recovery
    try {
      const existingEvents = JSON.parse(localStorage.getItem(STORAGE_KEYS.SESSION_DATA) || '[]');
      existingEvents.push(event);
      localStorage.setItem(STORAGE_KEYS.SESSION_DATA, JSON.stringify(existingEvents.slice(-100))); // Keep last 100 events
    } catch (error) {
      logger.warn('[SessionManager] Failed to persist event:', { error });
    }
  }

  getSessionEvents(): ActivityTrackingEvent[] {
    return [...this.activityEvents];
  }

  endSession(): void {
    this.trackEvent('session_end', {
      duration: this.getSessionDuration(),
      eventsCount: this.activityEvents.length,
    });
  }
}
